import { Box, Grid, Heading, Text, VStack } from "@chakra-ui/react"
import { FaBookOpen, FaHeart, FaShieldAlt, FaUserFriends } from "react-icons/fa"
import {
  Burst,
  DISPLAY_FONT,
  HeartDivider,
  INK,
  IllustratedCard,
  IllustratedScreen,
  LANDSCAPE_MQ,
  MeetYourCharacterPanel,
  PillButton,
  TopicTile,
} from "../components/IllustratedScreen"
import { SIDEWAYS_MQ } from "../components/mediaQueries"

interface DisclaimerScreenProps {
  onContinue: () => void
}

// Icons follow the subjects drawn on the client's mockup; the circle
// colours are sampled from it.
const topics = [
  { label: "Relationships", icon: FaHeart, bg: "#ee678a" },
  { label: "Sex and pressure", icon: FaUserFriends, bg: "#9a73c0" },
  { label: "Health and protection", icon: FaShieldAlt, bg: "#80ab49" },
  { label: "Education", icon: FaBookOpen, bg: "#f9b538" },
]

const DisclaimerScreen: React.FC<DisclaimerScreenProps> = ({ onContinue }) => (
  <IllustratedScreen>
    <IllustratedCard
      logoSrc="/screens/logo-my-safe-space.webp"
      logoAlt="My Safe Space"
      logoWidth={1000}
      logoHeight={693}
      // About 60% of the screen in portrait; as tall as fits in landscape;
      // small on a phone held sideways.
      logoSize={{
        portrait: "min(60vw, 480px)",
        landscape: "min(35vh, 300px)",
        sideways: "66px",
      }}
      logoPlacement="overlap"
      // The card's edge runs through the ribbon, as on the mockup: the
      // overlap is a fifth of the logo's height (0.693 x width in portrait).
      logoOverlap={{
        portrait: "min(8.7vw, 70px)",
        landscape: "min(7.35vh, 63px)",
        sideways: "18px",
      }}
      maxW={{ portrait: "616px", landscape: "1100px", sideways: "1100px" }}
    >
      {/* One column in portrait, as on the mockup; in landscape the story
          and topics sit on the left and the character and Continue on the
          right, so 1280x800 fits without scrolling. A phone held sideways
          has the same two columns, tighter, with the Continue button in a
          row of its own so it can stick to the bottom of the screen when
          the page has to scroll (the 640-wide phone). */}
      <Box
        display="grid"
        gridTemplateColumns="1fr"
        gap={4}
        sx={{
          [LANDSCAPE_MQ]: {
            gridTemplateColumns: "1.15fr 1fr",
            columnGap: "2rem",
            alignItems: "center",
          },
          [SIDEWAYS_MQ]: {
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.2fr)",
            gridTemplateRows: "auto minmax(0, 1fr)",
            columnGap: "16px",
            rowGap: "8px",
            alignItems: "start",
          },
        }}
      >
        {/* The mockup insets the story and tiles 24px more than the panel
            and button below them; the landscape columns have no room for it. */}
        <VStack
          spacing={3}
          align="stretch"
          px={{ base: 0, md: 6 }}
          sx={{
            [LANDSCAPE_MQ]: { paddingInline: 0 },
            [SIDEWAYS_MQ]: {
              gridArea: "1 / 1 / span 2 / span 1",
              paddingInline: 0,
              gap: "6px",
            },
          }}
        >
          <Heading
            as="h1"
            fontFamily={DISPLAY_FONT}
            fontSize={{ base: "clamp(24px, 7vw, 30px)", md: "36px" }}
            fontWeight={600}
            lineHeight="1.2"
            color={INK.heading}
            textAlign="center"
            sx={{ [SIDEWAYS_MQ]: { fontSize: "23px" } }}
          >
            Welcome to Your Safe Space
          </Heading>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color={INK.body}
            textAlign="center"
            lineHeight="1.5"
            mx="auto"
            sx={{
              maxWidth: "400px",
              [LANDSCAPE_MQ]: { maxWidth: "none" },
              [SIDEWAYS_MQ]: {
                maxWidth: "none",
                fontSize: "15px",
                lineHeight: "1.4",
              },
            }}
          >
            In this game, you will step into real-life situations that many
            girls face. This is a{" "}
            {/* Kept together so the last line reads as on the mockup. */}
            <Text as="span" whiteSpace="nowrap">
              <Text as="span" color={INK.violet} fontWeight="bold">
                SAFE SPACE
              </Text>{" "}
              to learn.
            </Text>
          </Text>

          <Box pt={2} sx={{ [SIDEWAYS_MQ]: { paddingTop: 0 } }}>
            <HeartDivider />
          </Box>

          <Text
            as="h2"
            fontFamily={DISPLAY_FONT}
            fontSize={{ base: "19px", md: "22px" }}
            fontWeight={600}
            lineHeight="1.2"
            color={INK.plum}
            textAlign="center"
            pt={2}
            sx={{ [SIDEWAYS_MQ]: { fontSize: "17px", paddingTop: 0 } }}
          >
            You will make choices about:
          </Text>
          <Grid
            templateColumns={{ base: "1fr", sm: "repeat(2, minmax(0, 1fr))" }}
            columnGap={4}
            rowGap={2}
            sx={{ [SIDEWAYS_MQ]: { columnGap: "10px", rowGap: "6px" } }}
          >
            {topics.map(topic => (
              <TopicTile key={topic.label} {...topic} />
            ))}
          </Grid>
        </VStack>

        {/* Sideways, the stack dissolves (display: contents) so the panel
            and the button are grid items of their own. */}
        <VStack
          spacing={3}
          align="stretch"
          sx={{ [SIDEWAYS_MQ]: { display: "contents" } }}
        >
          <MeetYourCharacterPanel
            titleMark={
              <Burst
                color="#e8809c"
                size="18px"
                right="-24px"
                top="-10px"
                transform="rotate(-35deg)"
              />
            }
            sx={{
              [SIDEWAYS_MQ]: {
                gridArea: "1 / 2",
                paddingBlock: "6px",
                paddingInline: "12px",
              },
            }}
          />
          <PillButton
            scheme="coral"
            onClick={onContinue}
            alignSelf="center"
            w="100%"
            maxW="310px"
            sx={{
              [SIDEWAYS_MQ]: {
                gridArea: "2 / 2",
                alignSelf: "end",
                justifySelf: "center",
                width: "100%",
                position: "sticky",
                bottom: "8px",
                height: "50px",
                fontSize: "20px",
                marginBottom: "4px",
              },
            }}
          >
            Continue
          </PillButton>
        </VStack>
      </Box>
    </IllustratedCard>
  </IllustratedScreen>
)

export default DisclaimerScreen
